package com.patronat.top.client.presentation.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.client.config.ClientAppConfiguration
import com.patronat.top.client.presentation.home.components.VenueReviewCard
import com.patronat.top.dto.ProfileSuccessResponse
import com.patronat.top.ui.atoms.AppButton
import com.patronat.top.ui.atoms.AppNetworkImage
import com.patronat.top.ui.atoms.ButtonType
import com.patronat.top.ui.molecules.inputs.AppInput
import com.patronat.top.ui.molecules.inputs.AppInputMode
import com.patronat.top.ui.molecules.inputs.AppTextArea
import com.patronat.top.ui.theme.AccentPink
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyLarge
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft400
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.auth_email_label
import top.clientapp.generated.resources.auth_first_name_label
import top.clientapp.generated.resources.auth_last_name_label
import top.clientapp.generated.resources.auth_phone_label
import top.clientapp.generated.resources.client_profile_app_version_prefix
import top.clientapp.generated.resources.client_profile_auth_required_message
import top.clientapp.generated.resources.client_profile_auth_required_title
import top.clientapp.generated.resources.client_profile_change_photo
import top.clientapp.generated.resources.client_profile_contact_description_label
import top.clientapp.generated.resources.client_profile_contact_description_placeholder
import top.clientapp.generated.resources.client_profile_contact_subject_label
import top.clientapp.generated.resources.client_profile_contact_subject_placeholder
import top.clientapp.generated.resources.client_profile_contact_us
import top.clientapp.generated.resources.client_profile_delete_account
import top.clientapp.generated.resources.client_profile_delete_reason_label
import top.clientapp.generated.resources.client_profile_delete_reason_placeholder
import top.clientapp.generated.resources.client_profile_delete_warning_message
import top.clientapp.generated.resources.client_profile_delete_warning_title
import top.clientapp.generated.resources.client_profile_edit
import top.clientapp.generated.resources.client_profile_general_info
import top.clientapp.generated.resources.client_profile_logout
import top.clientapp.generated.resources.client_profile_my_reviews
import top.clientapp.generated.resources.client_profile_no_reviews
import top.clientapp.generated.resources.client_profile_save
import top.clientapp.generated.resources.client_profile_sign_in
import top.clientapp.generated.resources.client_profile_unknown_user
import top.clientapp.generated.resources.client_profile_user_activity
import top.clientapp.generated.resources.client_venue_back_content_description

private val ProfilePrimaryAction = Color(0xFFB58A5A)
private val ProfilePhotoButtonBorder = Color(0xFFC4A57B)

@Composable
fun ClientProfileContent(
    state: ClientProfileViewState,
    onEvent: (ClientProfileEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    when (state.screen) {
        ClientProfileScreen.Main -> ClientProfileMainContent(
            state = state,
            onEvent = onEvent,
            modifier = modifier
        )
        ClientProfileScreen.EditProfile -> ClientEditProfileContent(
            state = state,
            onEvent = onEvent,
            modifier = modifier
        )
        ClientProfileScreen.MyReviews -> ClientMyReviewsContent(
            state = state,
            onEvent = onEvent,
            modifier = modifier
        )
        ClientProfileScreen.ContactUs -> ClientContactUsContent(
            state = state,
            onEvent = onEvent,
            modifier = modifier
        )
        ClientProfileScreen.DeleteAccount -> ClientDeleteAccountContent(
            state = state,
            onEvent = onEvent,
            modifier = modifier
        )
    }
}

@Composable
private fun ClientProfileMainContent(
    state: ClientProfileViewState,
    onEvent: (ClientProfileEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    when {
        state.requiresAuth -> {
            ProfileAuthRequired(
                onSignInClick = { onEvent(ClientProfileEvent.OnSignInClicked) },
                modifier = modifier
            )
        }
        state.isLoadingProfile && state.profile == null -> {
            Box(
                modifier = modifier
                    .fillMaxSize()
                    .background(White),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = Primary600)
            }
        }
        else -> {
            val profile = state.profile
            Column(
                modifier = modifier
                    .fillMaxSize()
                    .background(White)
            ) {
                LazyColumn(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    contentPadding = PaddingValues(start = 20.dp, top = 22.dp, end = 20.dp, bottom = 20.dp),
                    verticalArrangement = Arrangement.spacedBy(20.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    item {
                        ProfileHeader(profile = profile)
                    }

                    item {
                        ProfileSection(title = stringResource(Res.string.client_profile_general_info)) {
                            ProfileMenuRow(
                                title = stringResource(Res.string.client_profile_edit),
                                icon = AppIcons.Profile,
                                onClick = { onEvent(ClientProfileEvent.OnEditProfileClicked) }
                            )
                        }
                    }

                    item {
                        ProfileSection(title = stringResource(Res.string.client_profile_user_activity)) {
                            ProfileMenuRow(
                                title = stringResource(Res.string.client_profile_my_reviews),
                                icon = AppIcons.RatingStarFilled,
                                onClick = { onEvent(ClientProfileEvent.OnMyReviewsClicked) }
                            )
                            ProfileMenuDivider()
                            ProfileMenuRow(
                                title = stringResource(Res.string.client_profile_contact_us),
                                icon = AppIcons.Email,
                                onClick = { onEvent(ClientProfileEvent.OnContactUsClicked) }
                            )
                        }
                    }
                }

                ProfileMainFooter(
                    onLogoutClick = { onEvent(ClientProfileEvent.OnLogoutClicked) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(start = 20.dp, end = 20.dp, bottom = 10.dp)
                )
            }
        }
    }
}

@Composable
private fun ProfileMainFooter(
    onLogoutClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        ProfileTextAction(
            text = stringResource(Res.string.client_profile_logout),
            color = ProfilePrimaryAction,
            onClick = onLogoutClick
        )
        Spacer(modifier = Modifier.height(6.dp))
        Text(
            text = "${stringResource(Res.string.client_profile_app_version_prefix)} ${ClientAppConfiguration.VERSION_NAME}",
            style = BodyMedium.copy(
                color = MineShaft400,
                fontSize = 13.sp,
                lineHeight = 19.5.sp,
                fontWeight = FontWeight.Normal,
                textAlign = TextAlign.Center
            ),
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun ProfileTextAction(
    text: String,
    color: Color,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Text(
        text = text,
        style = BodyLarge.copy(
            color = color,
            fontSize = 20.sp,
            lineHeight = 32.sp,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center
        ),
        textAlign = TextAlign.Center,
        modifier = modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 8.dp)
    )
}

@Composable
private fun ProfileSecondaryDangerAction(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Text(
        text = text,
        style = BodyMedium.copy(
            color = AccentPink.copy(alpha = 0.82f),
            fontSize = 15.sp,
            lineHeight = 22.sp,
            fontWeight = FontWeight.Normal,
            textAlign = TextAlign.Center
        ),
        textAlign = TextAlign.Center,
        modifier = modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 6.dp)
    )
}

@Composable
private fun ProfileHeader(
    profile: ProfileSuccessResponse?,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        ProfileAvatar(
            avatarUrl = profile?.avatarUrl,
            initials = profile?.initials().orEmpty().ifBlank { "T" },
            size = 80.dp
        )

        Spacer(modifier = Modifier.height(12.dp))

        Text(
            text = profile?.displayName()?.takeIf { it.isNotBlank() }
                ?: stringResource(Res.string.client_profile_unknown_user),
            style = BodyLarge.copy(
                color = MineShaft900,
                fontSize = 20.sp,
                lineHeight = 30.sp,
                fontWeight = FontWeight.SemiBold,
                textAlign = TextAlign.Center
            ),
            textAlign = TextAlign.Center,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun ProfileAvatar(
    avatarUrl: String?,
    initials: String,
    size: Dp,
    modifier: Modifier = Modifier,
    showPlaceholderIcon: Boolean = false,
    isLoading: Boolean = false
) {
    Box(
        modifier = modifier
            .size(size)
            .clip(CircleShape)
            .background(MineShaft100),
        contentAlignment = Alignment.Center
    ) {
        val imageUrl = avatarUrl?.takeIf { it.isNotBlank() }
        if (imageUrl != null) {
            AppNetworkImage(
                model = imageUrl,
                contentDescription = null,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            )
        } else if (showPlaceholderIcon) {
            Icon(
                painter = AppIcons.Profile,
                contentDescription = null,
                tint = MineShaft200,
                modifier = Modifier.size(size * 0.46f)
            )
        } else {
            Text(
                text = initials.ifBlank { "T" },
                style = BodyLarge.copy(
                    color = MineShaft600,
                    fontSize = 32.sp,
                    lineHeight = 38.sp,
                    fontWeight = FontWeight.SemiBold
                )
            )
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(White.copy(alpha = 0.62f)),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(
                    color = Primary600,
                    strokeWidth = 2.dp,
                    modifier = Modifier.size(size * 0.34f)
                )
            }
        }
    }
}

@Composable
private fun EditProfileAvatarSection(
    avatarUrl: String?,
    initials: String,
    displayName: String,
    isUploading: Boolean,
    onChangeClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically
    ) {
        ProfileAvatar(
            avatarUrl = avatarUrl,
            initials = initials,
            size = 80.dp,
            showPlaceholderIcon = true,
            isLoading = isUploading
        )

        Spacer(modifier = Modifier.width(24.dp))

        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.Center
        ) {
            Text(
                text = displayName,
                style = BodyLarge.copy(
                    color = MineShaft900,
                    fontSize = 20.sp,
                    lineHeight = 30.sp,
                    fontWeight = FontWeight.SemiBold
                ),
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )

            Spacer(modifier = Modifier.height(8.dp))

            ChangePhotoButton(
                text = stringResource(Res.string.client_profile_change_photo),
                onClick = onChangeClick,
                isLoading = isUploading,
                modifier = Modifier
            )
        }
    }
}

@Composable
private fun ChangePhotoButton(
    text: String,
    onClick: () -> Unit,
    isLoading: Boolean,
    modifier: Modifier = Modifier
) {
    val shape = RoundedCornerShape(10.dp)
    Box(
        modifier = modifier
            .size(width = 120.445.dp, height = 32.988.dp)
            .clip(shape)
            .border(width = 0.75.dp, color = ProfilePhotoButtonBorder, shape = shape)
            .clickable(enabled = !isLoading, onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        if (isLoading) {
            CircularProgressIndicator(
                color = ProfilePhotoButtonBorder,
                strokeWidth = 2.dp,
                modifier = Modifier.size(16.dp)
            )
        } else {
            Text(
                text = text,
                style = BodyMedium.copy(
                    color = ProfilePhotoButtonBorder,
                    fontSize = 16.sp,
                    lineHeight = 24.sp,
                    fontWeight = FontWeight.SemiBold,
                    textAlign = TextAlign.Center
                ),
                textAlign = TextAlign.Center,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun ProfileSection(
    title: String,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(modifier = modifier.fillMaxWidth()) {
        Text(
            text = title,
            style = BodyMedium.copy(
                color = MineShaft950,
                fontSize = 16.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.Medium
            )
        )

        Spacer(modifier = Modifier.height(8.dp))

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(12.dp))
                .background(White)
                .border(1.dp, MineShaft100, RoundedCornerShape(12.dp))
        ) {
            content()
        }
    }
}

@Composable
private fun ProfileMenuRow(
    title: String,
    icon: Painter,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(64.dp)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(44.dp)
                .clip(RoundedCornerShape(12.dp))
                .background(MineShaft50),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = icon,
                contentDescription = null,
                tint = MineShaft500,
                modifier = Modifier.size(22.dp)
            )
        }

        Spacer(modifier = Modifier.width(12.dp))

        Text(
            text = title,
            style = BodyLarge.copy(
                color = MineShaft950,
                fontSize = 16.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.Normal
            ),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f)
        )

        Icon(
            painter = AppIcons.ArrowLeft,
            contentDescription = null,
            tint = MineShaft400,
            modifier = Modifier
                .size(20.dp)
                .rotate(180f)
        )
    }
}

@Composable
private fun ProfileMenuDivider() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(1.dp)
            .padding(start = 68.dp)
            .background(MineShaft100)
    )
}

@Composable
private fun ClientEditProfileContent(
    state: ClientProfileViewState,
    onEvent: (ClientProfileEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        ProfileTopBar(
            title = stringResource(Res.string.client_profile_edit),
            onBackClick = { onEvent(ClientProfileEvent.OnBackClicked) }
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 20.dp, vertical = 20.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            EditProfileAvatarSection(
                avatarUrl = state.editAvatarUrl,
                initials = state.profile?.initials().orEmpty().ifBlank { "T" },
                displayName = state.profile?.displayName()?.takeIf { it.isNotBlank() }
                    ?: stringResource(Res.string.client_profile_unknown_user),
                isUploading = state.isAvatarUploading,
                onChangeClick = { onEvent(ClientProfileEvent.OnEditAvatarClicked) },
                modifier = Modifier.fillMaxWidth()
            )

            AppInput(
                value = state.editFirstName,
                onValueChange = { onEvent(ClientProfileEvent.OnEditFirstNameChanged(it)) },
                label = stringResource(Res.string.auth_first_name_label),
                placeholder = stringResource(Res.string.auth_first_name_label)
            )
            AppInput(
                value = state.editLastName,
                onValueChange = { onEvent(ClientProfileEvent.OnEditLastNameChanged(it)) },
                label = stringResource(Res.string.auth_last_name_label),
                placeholder = stringResource(Res.string.auth_last_name_label)
            )
            AppInput(
                value = state.editPhone,
                onValueChange = {},
                label = stringResource(Res.string.auth_phone_label),
                placeholder = "+38 000 000 00 00",
                inputMode = AppInputMode.Phone,
                readOnly = true
            )
            AppInput(
                value = state.editEmail,
                onValueChange = { onEvent(ClientProfileEvent.OnEditEmailChanged(it)) },
                label = stringResource(Res.string.auth_email_label),
                placeholder = "youremail@gmail.com",
                leadingIcon = AppIcons.Email,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email)
            )

            Spacer(modifier = Modifier.height(12.dp))

            AppButton(
                text = stringResource(Res.string.client_profile_save),
                onClick = { onEvent(ClientProfileEvent.OnSaveProfileClicked) },
                type = ButtonType.Black,
                enabled = !state.isAvatarUploading,
                isLoading = state.isSavingProfile,
                modifier = Modifier.fillMaxWidth()
            )

            ProfileSecondaryDangerAction(
                text = stringResource(Res.string.client_profile_delete_account),
                onClick = { onEvent(ClientProfileEvent.OnDeleteAccountClicked) }
            )
        }
    }
}

@Composable
private fun ClientMyReviewsContent(
    state: ClientProfileViewState,
    onEvent: (ClientProfileEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        ProfileTopBar(
            title = stringResource(Res.string.client_profile_my_reviews),
            onBackClick = { onEvent(ClientProfileEvent.OnBackClicked) }
        )

        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(start = 20.dp, top = 18.dp, end = 20.dp, bottom = 22.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            when {
                state.isLoadingReviews && state.reviews.isEmpty() -> {
                    item {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = 48.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(color = Primary600)
                        }
                    }
                }
                state.reviews.isEmpty() -> {
                    item {
                        ProfileEmptyState(
                            title = stringResource(Res.string.client_profile_no_reviews),
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
                else -> {
                    items(
                        items = state.reviews,
                        key = { it.id }
                    ) { review ->
                        VenueReviewCard(
                            review = review,
                            isLikeLoading = false,
                            isLikeEnabled = false,
                            onPhotoClick = { onEvent(ClientProfileEvent.OnReviewClicked(review.venueId)) },
                            onLikeClick = {},
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { onEvent(ClientProfileEvent.OnReviewClicked(review.venueId)) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ClientContactUsContent(
    state: ClientProfileViewState,
    onEvent: (ClientProfileEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        ProfileTopBar(
            title = stringResource(Res.string.client_profile_contact_us),
            onBackClick = { onEvent(ClientProfileEvent.OnBackClicked) }
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 20.dp, vertical = 20.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            AppInput(
                value = state.contactSubject,
                onValueChange = { onEvent(ClientProfileEvent.OnContactSubjectChanged(it)) },
                label = stringResource(Res.string.client_profile_contact_subject_label),
                placeholder = stringResource(Res.string.client_profile_contact_subject_placeholder),
                leadingIcon = AppIcons.Email
            )

            AppTextArea(
                value = state.contactDescription,
                onValueChange = { onEvent(ClientProfileEvent.OnContactDescriptionChanged(it)) },
                label = stringResource(Res.string.client_profile_contact_description_label),
                placeholder = stringResource(Res.string.client_profile_contact_description_placeholder),
                minHeight = 156.dp,
                maxLines = 8
            )

            Spacer(modifier = Modifier.height(12.dp))

            AppButton(
                text = stringResource(Res.string.client_profile_save),
                onClick = { onEvent(ClientProfileEvent.OnSubmitContactClicked) },
                type = ButtonType.Black,
                isLoading = state.isSendingContact,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun ClientDeleteAccountContent(
    state: ClientProfileViewState,
    onEvent: (ClientProfileEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        ProfileTopBar(
            title = stringResource(Res.string.client_profile_delete_account),
            onBackClick = { onEvent(ClientProfileEvent.OnBackClicked) }
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 20.dp, vertical = 20.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            DeleteWarningBox(modifier = Modifier.fillMaxWidth())

            AppInput(
                value = state.deleteEmail,
                onValueChange = { onEvent(ClientProfileEvent.OnDeleteEmailChanged(it)) },
                label = stringResource(Res.string.auth_email_label),
                placeholder = "youremail@gmail.com",
                leadingIcon = AppIcons.Email,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email)
            )

            AppTextArea(
                value = state.deleteReason,
                onValueChange = { onEvent(ClientProfileEvent.OnDeleteReasonChanged(it)) },
                label = stringResource(Res.string.client_profile_delete_reason_label),
                placeholder = stringResource(Res.string.client_profile_delete_reason_placeholder),
                minHeight = 156.dp,
                maxLines = 8
            )

            Spacer(modifier = Modifier.height(12.dp))

            AppButton(
                text = stringResource(Res.string.client_profile_delete_account),
                onClick = { onEvent(ClientProfileEvent.OnConfirmDeleteAccountClicked) },
                type = ButtonType.Error,
                isLoading = state.isDeletingAccount,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun DeleteWarningBox(
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(10.dp))
            .background(AccentPink.copy(alpha = 0.08f))
            .border(1.dp, AccentPink.copy(alpha = 0.28f), RoundedCornerShape(10.dp))
            .padding(14.dp)
    ) {
        Text(
            text = stringResource(Res.string.client_profile_delete_warning_title),
            style = BodyLarge.copy(
                color = AccentPink,
                fontSize = 15.sp,
                lineHeight = 20.sp,
                fontWeight = FontWeight.SemiBold
            )
        )
        Spacer(modifier = Modifier.height(6.dp))
        Text(
            text = stringResource(Res.string.client_profile_delete_warning_message),
            style = BodyMedium.copy(
                color = MineShaft600,
                fontSize = 13.sp,
                lineHeight = 18.sp
            )
        )
    }
}

@Composable
private fun ProfileTopBar(
    title: String,
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(56.dp)
            .background(White)
            .padding(horizontal = 20.dp),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .align(Alignment.CenterStart)
                .size(40.dp)
                .clip(CircleShape)
                .clickable(onClick = onBackClick),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = AppIcons.ArrowLeft,
                contentDescription = stringResource(Res.string.client_venue_back_content_description),
                tint = MineShaft950,
                modifier = Modifier.size(22.dp)
            )
        }

        Text(
            text = title,
            style = BodyLarge.copy(
                color = MineShaft950,
                fontSize = 18.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.SemiBold,
                textAlign = TextAlign.Center
            ),
            textAlign = TextAlign.Center,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.padding(horizontal = 52.dp)
        )
    }
}

@Composable
private fun ProfileAuthRequired(
    onSignInClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
            .padding(horizontal = 28.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Text(
            text = stringResource(Res.string.client_profile_auth_required_title),
            style = BodyLarge.copy(
                color = MineShaft950,
                fontSize = 20.sp,
                lineHeight = 26.sp,
                fontWeight = FontWeight.SemiBold,
                textAlign = TextAlign.Center
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(8.dp))
        Text(
            text = stringResource(Res.string.client_profile_auth_required_message),
            style = BodyMedium.copy(
                color = MineShaft600,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                textAlign = TextAlign.Center
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(24.dp))
        AppButton(
            text = stringResource(Res.string.client_profile_sign_in),
            onClick = onSignInClick,
            type = ButtonType.Black,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun ProfileEmptyState(
    title: String,
    modifier: Modifier = Modifier
) {
    Text(
        text = title,
        style = BodyLarge.copy(
            color = MineShaft600,
            fontSize = 16.sp,
            lineHeight = 22.sp,
            textAlign = TextAlign.Center
        ),
        textAlign = TextAlign.Center,
        modifier = modifier.padding(top = 52.dp)
    )
}

private fun ProfileSuccessResponse.displayName(): String {
    return listOf(firstName, lastName)
        .map { it.trim() }
        .filter { it.isNotEmpty() }
        .joinToString(" ")
}

private fun ProfileSuccessResponse.initials(): String {
    val first = firstName.trim().firstOrNull()?.uppercaseChar()
    val last = lastName.trim().firstOrNull()?.uppercaseChar()
    return buildString {
        first?.let { append(it) }
        last?.let { append(it) }
    }
}
